import { useEffect, useState, useCallback } from "react";

export type Theme = "light" | "dark";

const STORAGE_KEY = "eartrainer:theme";

function getPreferredTheme(): Theme {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return "light";
    const stored = localStorage.getItem(STORAGE_KEY) as Theme | null;
    if (stored === "light" || stored === "dark") return stored;
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export default function useTheme() {
    const [theme, setThemeState] = useState<Theme>(() => getPreferredTheme());

    const applyTheme = useCallback((t: Theme) => {
        const root = document.documentElement;
        if (!root) return;
        root.setAttribute("data-theme", t);
    }, []);

    useEffect(() => {
        applyTheme(theme);
        try {
            localStorage.setItem(STORAGE_KEY, theme);
        } catch (e) {
            // ignore
        }
    }, [theme, applyTheme]);

    useEffect(() => {
        const mq = window.matchMedia("(prefers-color-scheme: dark)");
        const handler = (e: MediaQueryListEvent) => {
            // if user hasn't explicitly set a theme in localStorage, follow system
            try {
                const stored = localStorage.getItem(STORAGE_KEY);
                if (!stored) {
                    setThemeState(e.matches ? "dark" : "light");
                }
            } catch (err) {
                // ignore
            }
        };
        if (mq && typeof mq.addEventListener === "function") {
            mq.addEventListener("change", handler);
            return () => mq.removeEventListener("change", handler);
        } else if (mq && typeof (mq as any).addListener === "function") {
            // fallback
            (mq as any).addListener(handler);
            return () => (mq as any).removeListener(handler);
        }
    }, []);

    const setTheme = useCallback((t: Theme) => {
        setThemeState(t);
    }, []);

    const toggleTheme = useCallback(() => setThemeState((s) => (s === "light" ? "dark" : "light")), []);

    return { theme, setTheme, toggleTheme } as const;
}
